// src/services/apiUrl.ts — single source of truth for the backend API host.
//
// The base URL nominally comes from the build-time `REACT_APP_API_URL`. In the
// Cloudflare Workers deploy that value is a *build variable* with no committed
// .env to fall back on, and it has shipped wrong twice: a Railway preview host
// on dev, and the retired `api-88prod.80eight.io` on prod — each time every
// request died at DNS (`ERR_NAME_NOT_RESOLVED`) and the console white-screened
// to `/unauthorized`.
//
// So for the KNOWN deployed console origins the API host is fixed here and is
// AUTHORITATIVE — the build variable cannot override it. This is the intended,
// permanent pairing (office → api, office-dev → api-dev); a stale or wrong
// build var is simply ignored on those origins. Only local/preview/unknown
// origins (localhost dev server, one-off previews) fall through to whatever
// `REACT_APP_API_URL` is set to.

/** Deployed console origin → its stable, custom-domain API host (authoritative). */
const ORIGIN_TO_API_HOST: Readonly<Record<string, string>> = {
  "https://office.80eight.io": "https://api.80eight.io",
  "https://office-dev.80eight.io": "https://api-dev.80eight.io",
};

/**
 * Resolve the backend API base URL.
 *
 * A recognised console origin wins outright — the build variable is ignored on
 * those origins (it has proven unreliable). Every other origin (localhost dev
 * server, preview builds) uses the configured `REACT_APP_API_URL`.
 * Parameterised for testability; production callers use the defaults.
 */
export const resolveApiBaseUrl = (
  configured: string | undefined = process.env.REACT_APP_API_URL,
  origin: string = typeof window !== "undefined" ? window.location.origin : ""
): string | undefined => {
  const authoritative = ORIGIN_TO_API_HOST[origin];
  if (authoritative) {
    if (
      typeof console !== "undefined" &&
      configured &&
      configured !== authoritative
    ) {
      console.warn(
        `[apiUrl] Ignoring REACT_APP_API_URL ("${configured}") on ${origin}; ` +
          `using the pinned API host ${authoritative}.`
      );
    }
    return authoritative;
  }
  return configured;
};
